import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  InputBase,
  styled,
  Typography,
  CircularProgress,
  Paper,
  Tooltip,
  IconButton,
  Popper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Alert,
  Stack
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as SearchIcon } from '../../../public/static/ai/search_icon.svg';
import { ReactComponent as SendIcon } from '../../../public/static/ai/send_icon.svg';
import { ReactComponent as ChanakyaBubble } from '../../../public/static/ai/chanakya_bubble.svg';
import { ReactComponent as PredictionIcon } from '../../../public/static/ai/prediction_icon.svg';
import { ReactComponent as InsightIcon } from '../../../public/static/ai/insight_icon.svg';

import {
  parseCommand,
  executeAICommand,
  getAvailableCommands,
  fetchDummyAIAgentSuggestion,
  getDummyPrediction,
  getDummyInsight
} from './aiAgent/AIAgentService';
import '../../css/GlobalStyles.css';
import { useAuth } from '../customContextProviders/AuthContext';

const glassBase = {
  '--glass-frost': 0.18,
  '--glass-saturation': 1.4,
  background: 'light-dark(hsl(0 0% 100% / var(--glass-frost)), hsl(0 0% 0% / var(--glass-frost)))',
  backdropFilter: 'blur(18px) saturate(var(--glass-saturation))',
  WebkitBackdropFilter: 'blur(18px) saturate(var(--glass-saturation))',
  border: '1px solid rgba(255,255,255,0.35)',
  boxShadow: `
    0 0 2px 1px rgba(255,255,255,0.25) inset,
    0 0 10px 4px rgba(255,255,255,0.15) inset,
    0px 4px 12px rgba(0,0,0,0.12),
    inset 0 1px 0 rgba(255,255,255,0.3),
    inset 0 -1px 0 rgba(255,255,255,0.15)
  `
};

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '100%',
      '&:focus': {
        width: '100%'
      }
    }
  }
}));

/* ===========================================================
   🔵 7) MAIN SCREEN
=========================================================== */
export default function ChanakyaScreen({ setBackground, isPrediction }) {
  const navigate = useNavigate();
  const { sessionUserData } = useAuth();
  const inputRef = useRef(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [lastCommand, setLastCommand] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const recognitionRef = useRef(null);
  const [aiAnchorEl, setAiAnchorEl] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [mode, setMode] = useState('');

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.onresult = (event) => {
        const { transcript } = event.results[0][0];
        setInput(transcript);
        setIsListening(false);
        handleCommand(transcript);
      };
      recognitionRef.current.onerror = () => {
        setIsListening(false);
        setFeedback({
          type: 'error',
          message: 'Error in speech recognition'
        });
      };
    }
  }, []);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleSuggestionsOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setShowSuggestions(true);
  };

  const handleSuggestionsClose = () => {
    setShowSuggestions(false);
    setAnchorEl(null);
  };

  const handleVoiceInput = () => {
    if (recognitionRef.current) {
      if (isListening) {
        recognitionRef.current.stop();
        setIsListening(false);
      } else {
        setIsListening(true);
        setInput('');
        recognitionRef.current.start();
      }
    }
  };

  const handleCommand = async (commandText) => {
    if (!commandText.trim()) {
      setFeedback({
        type: 'warning',
        message: 'Please enter a command'
      });
      return;
    }

    setIsLoading(true);
    setFeedback(null);

    try {
      const result = await executeAICommand(
        sessionUserData?.user?.role,
        commandText,
        navigate,
        (modalType) => {
          // Dispatch custom event for modal opening
          const event = new CustomEvent('aiAgentOpenModal', {
            detail: { modalType }
          });
          window.dispatchEvent(event);
        }
      );

      setLastCommand({
        input: commandText,
        result
      });

      setFeedback({
        type: result.success ? 'success' : 'error',
        message: result.message
      });

      if (result.success) {
        setInput('');
        // Auto clear feedback after 3 seconds
        setTimeout(() => setFeedback(null), 3000);
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        message: 'An error occurred while processing your command'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      e.preventDefault();
      handleCommand(input);
    }
  };

  const handleSuggestionClick = (pattern) => {
    setInput(pattern);
    handleSuggestionsClose();
    setTimeout(() => handleCommand(pattern), 100);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'ai-agent-suggestions' : undefined;

  const commands = getAvailableCommands(sessionUserData?.user?.role);

  const handleAIClick = async (type) => {
    setAiLoading(true);
    setAiSuggestion(null);

    const page = window.location?.pathname || '/dashboard/soundboxT';

    try {
      const res = await fetchDummyAIAgentSuggestion(page, type);

      setAiSuggestion({
        type: res.type,
        page: res.page,
        ...res.suggestion
      });
    } finally {
      setAiLoading(false);
    }
  };

  const handleAIClose = () => {
    setAiAnchorEl(null);
    setAiSuggestion(null);
  };
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '54%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        zIndex: 9
      }}
    >
      {/* GLASS SCREEN */}
      <Box className="glass-screen">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '33vh' // or fixed height if needed
          }}
        >
          <>
            {aiLoading && (
              <div className="chanakya-wrapper">
                <ChanakyaBubble />
              </div>
            )}

            {!aiLoading && aiSuggestion && (
              <Box sx={{ ...glassBase, borderRadius: 1, p: 2, height: '100%', overflowY: 'auto' }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 13,
                      color: mode === 'prediction' ? '#135FC9' : '#4A29AA'
                    }}
                  >
                    {aiSuggestion.title}
                  </Typography>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <Typography
                    sx={{ fontSize: 12, color: mode === 'prediction' ? '#135FC9' : '#4A29AA' }}
                  >
                    {aiSuggestion.summary}
                  </Typography>

                  {aiSuggestion.recommendedAction && (
                    <Box sx={{ ...glassBase, borderRadius: 1, p: 1, mt: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: mode === 'prediction' ? '#135FC9' : '#4A29AA'
                        }}
                      >
                        {aiSuggestion.recommendedAction}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {!aiLoading && !aiSuggestion && <ChanakyaBubble />}
          </>
        </Box>
        <Box
          sx={{
            height: '14vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              width: '50%',
              mt: 1,
              mx: 'auto', // 👈 centers the box horizontally
              p: 2,
              ...glassBase,
              borderRadius: '50px',
              maxHeight: '40px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              overflowY: 'auto',
              cursor: 'pointer',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                width: 0,
                height: 0
              }
            }}
            onClick={(e) => {
              handleAIClick('prediction', e);
              setBackground(
                'linear-gradient(180deg, rgba(119, 200, 255, 0) 0%, rgba(126, 181, 252, 0.63) 25%, rgba(0, 66, 233, 0.48) 86%)'
              );
              setMode('prediction');
            }}
          >
            <PredictionIcon />
            <Typography
              sx={{
                color: '#135FC9',
                fontSize: '14px',
                fontWeight: 700,
                whiteSpace: 'nowrap'
              }}
            >
              Prediction
            </Typography>
          </Box>
          <Box
            sx={{
              width: '50%',
              mt: 1,
              mx: 'auto',
              p: 2,
              ...glassBase,
              borderRadius: '50px',
              maxHeight: '40px',
              display: 'flex',
              flexDirection: 'row',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              overflowY: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                width: 0,
                height: 0
              }
            }}
            onClick={(e) => {
              handleAIClick('insight', e);
              setBackground(
                'linear-gradient(180deg, rgba(168, 178, 241, 0) 0%, rgba(157, 168, 235, 0.63) 40%, rgba(72, 55, 195, 0.48) 100%)'
              );
              setMode('insight');
            }}
          >
            <InsightIcon />
            <Typography
              sx={{
                color: '#AD2AEF',
                fontSize: '14px',
                fontWeight: 700,
                whiteSpace: 'nowrap'
              }}
            >
              Insight
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            mt: 2,
            p: 2,
            ...glassBase,
            borderRadius: '12px',
            height: '32vh',
            maxHeight: '32vh',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              width: 0,
              height: 0
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              minHeight: '100%',
              alignContent: 'flex-end', // ✅ THIS pushes ALL rows to bottom
              alignItems: 'flex-end' // keeps items aligned nicely
            }}
          >
            <Box
              onClick={(e) => handleAIClick('riskManagement', e)}
              sx={{
                ...glassBase,
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: 'rgba(41, 128, 255, 0.08)',
                width: 'calc(50% - 6px)',
                textAlign: 'center',
                cursor: 'pointer'
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#014CB3',
                  lineHeight: '12px'
                }}
              >
                Risk <br/> Management
              </Typography>
            </Box>
            <Box
              onClick={(e) => handleAIClick('fraudDetection', e)}
              sx={{
                ...glassBase,
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: 'rgba(41, 128, 255, 0.08)',
                width: 'calc(50% - 6px)',
                textAlign: 'center',
                cursor: 'pointer'
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#014CB3',
                  lineHeight: '12px'
                }}
              >
                Fraud <br/>Detection
              </Typography>
            </Box>
            {commands.slice(0, 3).map((cmd, idx) => (
              <Box
                key={idx}
                onClick={() => handleSuggestionClick(cmd.examplePatterns[0])}
                sx={{
                  ...glassBase,
                  p: 1.5,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(41, 128, 255, 0.08)',
                  width: idx < 2 ? 'calc(50% - 6px)' : '100%',
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#014CB3',
                    lineHeight: '12px'
                  }}
                >
                  {idx < 2
                    ? cmd.description.split(' ').map((word, i) => (
                        <span key={i} style={{ display: 'block' }}>
                          {word}
                        </span>
                      ))
                    : cmd.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box>
        <Box
          sx={{
            width: '310px',
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <Card
            sx={{
              width: '100%',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #6EC6FF, #2980FF)',
              padding: '2px',
              boxShadow: '0 4px 20px rgba(41, 128, 255, 0.3)'
            }}
          >
            <Box
              sx={{
                background: '#ffffff',
                borderRadius: '10px',
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                px: 2,
                gap: 1
              }}
            >
              <SearchIcon />

              <StyledInputBase
                ref={inputRef}
                placeholder="Ask Chanakya...."
                value={input}
                onChange={handleInputChange}
                onKeyPress={handleSubmit}
                onFocus={handleSuggestionsOpen}
                disabled={isLoading}
                sx={{
                  flex: 1,
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#333'
                }}
              />

              {isLoading && <CircularProgress size={20} />}

              {!isLoading && (
                <Tooltip title="Voice Command">
                  <IconButton
                    size="small"
                    onClick={handleVoiceInput}
                    sx={{
                      color: isListening ? '#2980FF' : '#999',
                      opacity: isListening ? 1 : 0.6,
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    <MicIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}

              <IconButton
                size="small"
                onClick={handleSubmit}
                disabled={isLoading}
                sx={{
                  color: isLoading ? '#ccc' : '#2980FF',
                  '&:hover': { color: '#2980FF' }
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Card>
        </Box>

        {/* FEEDBACK ALERT */}
        {feedback && (
          <Box
            sx={{
              position: 'fixed',
              top: 80,
              right: 20,
              zIndex: 1300,
              maxWidth: 300,
              animation: 'slideIn 0.3s ease-in-out'
            }}
          >
            <Alert
              severity={feedback.type}
              onClose={() => setFeedback(null)}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setFeedback(null)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              {feedback.message}
            </Alert>
          </Box>
        )}

        {/* LISTENING INDICATOR */}
        {isListening && (
          <Box
            sx={{
              position: 'fixed',
              top: 100,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1300
            }}
          >
            <Paper
              sx={{
                ...glassBase,
                px: 2,
                py: 1,
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                borderRadius: '8px'
              }}
            >
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#0F6796' }}>
                Listening...
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>

      {/* AI Suggestion Popper */}
      {/* <Popper
        open={Boolean(aiAnchorEl)}
        anchorEl={aiAnchorEl}
        placement="bottom-start"
        sx={{ zIndex: 1400 }}
      >
        <Paper sx={{ p: 2, width: 300, ...glassBase, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
              {aiLoading ? 'Loading...' : aiSuggestion?.title || 'Suggestion'}
            </Typography>
            <IconButton size="small" onClick={handleAIClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {!aiLoading && aiSuggestion && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#333' }}>{aiSuggestion.summary}</Typography>
              {aiSuggestion.recommendedAction && (
                <Chip label={aiSuggestion.recommendedAction} size="small" sx={{ mt: 1 }} />
              )}
              {aiSuggestion?.confidence && (
                <Typography sx={{ fontSize: 11, color: '#666', mt: 1 }}>
                  Confidence: {aiSuggestion.confidence}
                </Typography>
              )}

              {typeof aiSuggestion?.score === 'number' && (
                <Typography sx={{ fontSize: 11, color: '#666', mt: 1 }}>
                  Confidence: {Math.round(aiSuggestion.score * 100)}%
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Popper> */}
    </Box>
  );
}
